import axios, { AxiosResponse, AxiosError, AxiosRequestConfig } from 'axios';
import { HttpStatus } from 'consts/HttpStatus';
import { ReactError } from 'consts/ReactError';

export type ResponseParams<T> = {
	status: number;
	data?: T;
};

export type ApiError = {
	error_code: string;
	message: string;
	error_detail?: any;
};

export const isErrorRes = (obj: any): obj is ApiError => {
	return 'error_code' in obj;
};

const INTERNAL_SERVER_ERROR = {
	error_code: '',
	message: ReactError.E9999,
};

/**
 * GET request
 * @param path パス（例：/admins）
 * @param params QueryString
 * @returns ResponseParams
 */
export const requestHttpGet = async <T>(path: string, params?: any): Promise<ResponseParams<T>> => {
	const config: AxiosRequestConfig = {
		params: {
			...params,
			_: Date.now(), // IE11cache回避用uniqueパラメータ
		},
	};
	return requestCommon(axios.get(path, config));
};

/**
 * POST request
 * @param path パス（例：/admins）
 * @param params RequestBody
 * @returns ResponseParams
 */
export const requestHttpPost = async <T>(path: string, params: any): Promise<ResponseParams<T>> => {
	return requestCommon(axios.post(path, params, { headers: { 'X-Requested-With': 'XMLHttpRequest' } }));
};

/**
 * PUT request
 * @param path パス（例：/admins/id）
 * @param params RequestBody
 * @returns ResponseParams
 */
export const requestHttpPut = async <T>(path: string, params: any): Promise<ResponseParams<T>> => {
	return requestCommon(axios.put(path, params));
};

/**
 * DELETE request
 * @param path パス（例：/admins）
 * @param params QueryString
 * @returns ResponseParams
 */
export const requestHttpDelete = async <T>(path: string, params?: any): Promise<ResponseParams<T>> => {
	return requestCommon(axios.delete(path, params));
};

/**
 * Axios通信共通処理
 * @param axiosResponse それぞれのHTTPMethodを使ったAxios通信のレスポンス
 * @returns ResponseParams
 */
const requestCommon = async <T>(axiosResponse: Promise<AxiosResponse<T>>) => {
	const result: ResponseParams<T> = {
		status: HttpStatus.OK,
	};

	await axiosResponse
		.then(function (response: AxiosResponse<T>) {
			result.status = response.status;
			result.data = response.data;
		})
		.catch(function (error: AxiosError<any>) {
			const response = error.response;
			result.status = response ? response.status : HttpStatus.INTERNAL_SERVER_ERROR;
			result.data = (response ? response.data : INTERNAL_SERVER_ERROR) as T;
		})
		.then(function () {});

	return result;
};
